import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Chip } from "@mui/material";
import { PlayArrow, Stop } from "@mui/icons-material";
import apiService from "../../../service/Api";
import DonationsTable from "../../stream-management/components/memorial-page/components/chema/DonationsTable";
import CampaignStatsCards from "./components/CampaignStatsCards";
import type { CampaignStats } from "./components/CampaignStatsCards";
import OrganiserCard from "./components/OrganiserCard";
import CampaignApprovalBanner from "./components/CampaignApprovalBanner";
import type { CampaignApprovalData } from "./components/CampaignApprovalBanner";
import PayoutDetailsCard from "./components/PayoutDetailsCard";
import type { PayoutDetail } from "./components/PayoutDetailsCard";
import CampaignCauseCard from "./components/CampaignCauseCard";
import type { CampaignCauseData } from "./components/CampaignCauseCard";
import WithdrawalBanner from "./components/WithdrawalBanner";
import type { WithdrawalRequest } from "./components/WithdrawalBanner";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface CampaignDetail extends CampaignApprovalData, CampaignCauseData {
  id: number;
  title: string;
  status: string;
  currency: string;
  amount_raised: string;
  donor_count: number;
  owner_display_name: string;
  owner_email: string | null;
  owner_phone: string | null;
  fundraiser_type: string;
  goal_amount: string | null;
  created_at: string;
  payout_details?: PayoutDetail[] | null;
}

interface DonationSummary {
  amount: string;
  paid: boolean;
}

interface PaginatedResponse<T> {
  results: T[];
  next: string | null;
}

const PUBLISHED = "published";

async function fetchAllPages<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean>,
): Promise<T[]> {
  const all: T[] = [];
  let url: string | null = endpoint;
  let firstPage = true;
  while (url) {
    const res: { data: PaginatedResponse<T> } = await apiService.get(
      url,
      firstPage ? params : undefined,
    );
    const data = res.data;
    if (Array.isArray(data)) {
      all.push(...(data as unknown as T[]));
      break;
    }
    all.push(...(data.results || []));
    if (data.next) {
      try {
        url = new URL(data.next).pathname + new URL(data.next).search;
      } catch {
        url = null;
      }
    } else {
      url = null;
    }
    firstPage = false;
  }
  return all;
}

const CampaignInsight = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { hasPermission } = usePermissions();

  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<
    WithdrawalRequest[]
  >([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [withdrawalActionLoading, setWithdrawalActionLoading] = useState(false);
  const [donationsRefreshKey, setDonationsRefreshKey] = useState(0);

  const fetchCampaignData = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [campaignRes, donationsRaw, withdrawalsRaw] = await Promise.all([
        apiService.get<CampaignDetail>(`/api/chema/campaigns/${campaignId}/`),
        fetchAllPages<DonationSummary>("/api/chema/donations/", {
          campaign_id: campaignId,
        }),
        fetchAllPages<WithdrawalRequest>("/api/chema/withdrawals/", {
          campaign: campaignId,
          status: "requested",
        }),
      ]);

      const c = campaignRes.data;
      setCampaign(c);
      setPendingWithdrawals(withdrawalsRaw);

      const totalPaid = donationsRaw
        .filter((d) => d.paid)
        .reduce((s, d) => s + parseFloat(d.amount || "0"), 0);

      const totalPending = donationsRaw
        .filter((d) => !d.paid)
        .reduce((s, d) => s + parseFloat(d.amount || "0"), 0);

      // We'll re-fetch approved withdrawals separately for the withdrawn stat
      const approvedWithdrawals = await fetchAllPages<WithdrawalRequest>(
        "/api/chema/withdrawals/",
        {
          campaign: campaignId,
          status__in: "approved,paid",
        },
      );

      const totalWithdrawn = approvedWithdrawals.reduce(
        (s, w) => s + parseFloat(w.amount || "0"),
        0,
      );

      setStats({
        totalPaid,
        totalPending,
        amountRaised: parseFloat(c.amount_raised || "0"),
        totalWithdrawn,
        currency: c.currency,
        donorCount: c.donor_count,
      });
    } catch (err) {
      console.error("Error fetching campaign data:", err);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]);

  const handleToggleStatus = async () => {
    if (!campaign) return;
    const newStatus = campaign.status === PUBLISHED ? "paused" : PUBLISHED;
    setTogglingStatus(true);
    try {
      await apiService.patch(`/api/chema/campaigns/${campaign.id}/`, {
        status: newStatus,
      });
      setCampaign((prev) => (prev ? { ...prev, status: newStatus } : prev));
    } catch (err) {
      console.error("Error toggling campaign status:", err);
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleWithdrawalAction = async (
    id: number,
    newStatus: "approved" | "rejected",
    proof?: File,
  ) => {
    setWithdrawalActionLoading(true);
    try {
      if (proof) {
        const formData = new FormData();
        formData.append("status", newStatus);
        formData.append("proof_of_payment", proof, proof.name);
        await apiService.patch(`/api/chema/withdrawals/${id}/`, formData);
      } else {
        await apiService.patch(`/api/chema/withdrawals/${id}/`, {
          status: newStatus,
        });
      }
      setPendingWithdrawals((prev) => prev.filter((w) => w.id !== id));
      if (newStatus === "approved") {
        // Refresh stats to reflect the new withdrawn total
        await fetchCampaignData();
        setDonationsRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error("Error updating withdrawal:", err);
      throw err;
    } finally {
      setWithdrawalActionLoading(false);
    }
  };

  const handleApproveWithdrawal = (id: number, proof: File) =>
    handleWithdrawalAction(id, "approved", proof);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Campaign not found.</Typography>
      </Box>
    );
  }

  const isPublished = campaign.status === PUBLISHED;
  const approvalStatus = campaign.approval_status ?? "approved";
  const isApproved = approvalStatus === "approved";
  // A campaign can't be switched on until an admin has approved it.
  const statusLabel =
    approvalStatus === "pending"
      ? "Awaiting approval"
      : approvalStatus === "rejected"
        ? "Rejected"
        : campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography fontWeight="bold" noWrap sx={{ maxWidth: 300 }}>
            {campaign.title}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: !isApproved
                ? approvalStatus === "pending"
                  ? "#fef3c7"
                  : "#fee2e2"
                : isPublished
                  ? "#d1fae5"
                  : "#fee2e2",
              color: !isApproved
                ? approvalStatus === "pending"
                  ? "#92400e"
                  : "#991b1b"
                : isPublished
                  ? "#065f46"
                  : "#991b1b",
              fontWeight: 500,
            }}
          />
        </Box>

        <Button
          variant="outlined"
          startIcon={
            togglingStatus ? (
              <CircularProgress size={14} color="inherit" />
            ) : isPublished ? (
              <Stop />
            ) : (
              <PlayArrow />
            )
          }
          disabled={
            !hasPermission(PERMISSIONS.DONATION_CHANGE) ||
            togglingStatus ||
            !isApproved
          }
          onClick={handleToggleStatus}
          sx={{
            textTransform: "none",
            borderRadius: 3,
            borderColor: isPublished ? "#ef4444" : "#10b981",
            color: isPublished ? "#ef4444" : "#10b981",
            bgcolor: isPublished ? "#fef2f2" : "#ecfdf5",
            "&:hover": {
              borderColor: isPublished ? "#dc2626" : "#059669",
              bgcolor: isPublished ? "#fee2e2" : "#d1fae5",
            },
          }}
        >
          {isPublished ? "Deactivate Fund" : "Activate Fund"}
        </Button>
      </Box>

      <CampaignApprovalBanner
        campaign={campaign}
        onChanged={fetchCampaignData}
      />

      <OrganiserCard
        name={campaign.owner_display_name}
        email={campaign.owner_email}
        phone={campaign.owner_phone}
      />

      {campaign.payout_details && (
        <PayoutDetailsCard details={campaign.payout_details} />
      )}

      <CampaignCauseCard campaign={campaign} />

      {/* Stats */}
      {stats && <CampaignStatsCards stats={stats} />}

      {/* Pending withdrawal banners */}
      {pendingWithdrawals.map((w) => (
        <WithdrawalBanner
          key={w.id}
          withdrawal={w}
          actionLoading={withdrawalActionLoading}
          onApprove={handleApproveWithdrawal}
          onDismiss={(id) => handleWithdrawalAction(id, "rejected")}
        />
      ))}

      {/* Donations table */}
      <DonationsTable
        campaignId={campaign.id}
        title={`Chema Donations List`}
        ordering="-created_at"
        refreshKey={donationsRefreshKey}
        enableDonorDetail
      />
    </Box>
  );
};

export default CampaignInsight;
